import { Fragment, useState, useEffect, useCallback } from 'react';
import {
  Loader2, TrendingUp, Trophy, Target, Users,
  ChevronDown, ChevronRight, IndianRupee,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';

// ==================== Types ====================

interface DealDetail {
  id: string;
  title: string;
  value: number;
  stage: string;
  updatedAt?: string;
  wonAt?: string;
  contact: string | null;
  account: string | null;
}

interface UserIncentive {
  userId: string;
  userName: string;
  email: string;
  qualifiedLeads: number;
  meetingsFixed: number;
  dealsWon: number;
  leadIncentive: number;
  meetingIncentive: number;
  closureIncentive: number;
  totalIncentive: number;
  bonusAmount: number;
  grandTotal: number;
  targetAchievement: number;
  bonusEligible: boolean;
  qualifyingDeals: DealDetail[];
  meetingDealsList: DealDetail[];
  closedDealsList: DealDetail[];
}

interface TeamData {
  period: string;
  rates: {
    qualifiedLead: number;
    meetingFixed: number;
    closure: number;
    targetBonusPct: number;
  };
  team: UserIncentive[];
  summary: {
    totalTeamIncentive: number;
    totalClosures: number;
    avgTargetAchievement: number;
    bestPerformer: { userId: string; name: string; total: number } | null;
    teamSize: number;
  };
}

// ==================== Helpers ====================

function getTargetColor(pct: number): string {
  if (pct >= 100) return 'bg-green-100 text-green-800';
  if (pct >= 75) return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
}

function getTargetBg(pct: number): string {
  if (pct >= 100) return 'bg-green-500';
  if (pct >= 75) return 'bg-yellow-500';
  return 'bg-red-500';
}

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function getMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  // Last 12 months
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
    options.push({ value, label });
  }
  return options;
}

// ==================== Component ====================

export function IncentivesPage() {
  const [month, setMonth] = useState(getCurrentMonth());
  const [data, setData] = useState<TeamData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/incentives/team?month=${month}`);
      setData(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load incentives');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleRow = (userId: string) => {
    setExpandedRow(prev => (prev === userId ? null : userId));
  };

  const monthOptions = getMonthOptions();

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between max-sm:flex-wrap max-sm:gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Incentives</h1>
          <p className="text-sm text-gray-500">Track sales team performance and incentive payouts</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 max-sm:min-h-[40px] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          >
            {monthOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          <span className="ml-2 text-sm text-gray-500">Loading incentives...</span>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 text-red-500">
          <p className="text-sm">{error}</p>
          <button onClick={fetchData} className="mt-2 text-sm text-[var(--ink-0)] hover:underline max-sm:min-h-[40px]">Retry</button>
        </div>
      ) : data ? (
        <>
          {/* Summary Cards */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 max-sm:grid-cols-2 max-sm:gap-3">
            {/* Total Team Incentive */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm max-sm:p-3">
              <div className="flex items-center gap-3 max-sm:flex-col max-sm:items-start max-sm:gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                  <IndianRupee size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500">Total Team Incentive</p>
                  <p className="text-xl font-bold text-gray-900">{formatINR(data.summary.totalTeamIncentive)}</p>
                </div>
              </div>
            </div>

            {/* Best Performer */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm max-sm:p-3">
              <div className="flex items-center gap-3 max-sm:flex-col max-sm:items-start max-sm:gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-yellow-100">
                  <Trophy size={20} className="text-yellow-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500">Best Performer</p>
                  <p className="text-lg font-bold text-gray-900">
                    {data.summary.bestPerformer?.name ?? 'N/A'}
                  </p>
                  {data.summary.bestPerformer && (
                    <p className="text-xs text-gray-400">{formatINR(data.summary.bestPerformer.total)}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Avg Target Achievement */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm max-sm:p-3">
              <div className="flex items-center gap-3 max-sm:flex-col max-sm:items-start max-sm:gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                  <Target size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500">Avg Target Achievement</p>
                  <p className="text-xl font-bold text-gray-900">{data.summary.avgTargetAchievement}%</p>
                </div>
              </div>
            </div>

            {/* Total Closures */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm max-sm:p-3">
              <div className="flex items-center gap-3 max-sm:flex-col max-sm:items-start max-sm:gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
                  <TrendingUp size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500">Total Closures</p>
                  <p className="text-xl font-bold text-gray-900">{data.summary.totalClosures}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Incentive Rate Reference */}
          <div className="mb-6 flex flex-wrap gap-3">
            <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
              Qualified Lead: {formatINR(data.rates.qualifiedLead)}
            </span>
            <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
              Meeting Fixed: {formatINR(data.rates.meetingFixed)}
            </span>
            <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
              Closure: {formatINR(data.rates.closure)}
            </span>
            <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
              Target Bonus: {data.rates.targetBonusPct}% (if target exceeded)
            </span>
          </div>

          {/* Team Table */}
          {data.team.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white py-16 text-gray-400">
              <Users size={40} className="mb-2" />
              <p className="text-sm">No sales team data for this period</p>
              <p className="text-xs">Assign deals to team members to see incentive calculations</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm max-sm:overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 w-8 max-sm:sticky max-sm:left-0 max-sm:z-10 max-sm:w-9 max-sm:min-w-[36px] max-sm:bg-gray-50 max-sm:px-2.5" />
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 max-sm:sticky max-sm:left-9 max-sm:z-10 max-sm:bg-gray-50 max-sm:pl-1 max-sm:shadow-[1px_0_0_#e5e7eb]">Name</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 text-center">Qualified Leads</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 text-center">Meetings</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 text-center">Closures</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 text-right">Lead INR</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 text-right">Meeting INR</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 text-right">Closure INR</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 text-right">Bonus</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 text-right">Total INR</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 text-center">Target %</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.team.map((member) => (
                    <Fragment key={member.userId}>
                      <tr
                        className="border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => toggleRow(member.userId)}
                      >
                        <td className="px-4 py-3 text-gray-400 max-sm:sticky max-sm:left-0 max-sm:z-10 max-sm:w-9 max-sm:min-w-[36px] max-sm:bg-white max-sm:px-2.5">
                          {expandedRow === member.userId ? (
                            <ChevronDown size={16} />
                          ) : (
                            <ChevronRight size={16} />
                          )}
                        </td>
                        <td className="px-4 py-3 max-sm:sticky max-sm:left-9 max-sm:z-10 max-sm:bg-white max-sm:pl-1 max-sm:shadow-[1px_0_0_#e5e7eb]">
                          <div className="max-sm:w-[128px]">
                            <p className="font-medium text-gray-900 max-sm:truncate">{member.userName}</p>
                            <p className="text-xs text-gray-400 max-sm:truncate">{member.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center font-medium text-gray-700">{member.qualifiedLeads}</td>
                        <td className="px-4 py-3 text-center font-medium text-gray-700">{member.meetingsFixed}</td>
                        <td className="px-4 py-3 text-center font-medium text-gray-700">{member.dealsWon}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{formatINR(member.leadIncentive)}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{formatINR(member.meetingIncentive)}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{formatINR(member.closureIncentive)}</td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {member.bonusAmount > 0 ? formatINR(member.bonusAmount) : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-gray-900">{formatINR(member.grandTotal)}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="h-2 w-16 rounded-full bg-gray-200 overflow-hidden">
                              <div
                                className={`h-full rounded-full ${getTargetBg(member.targetAchievement)}`}
                                style={{ width: `${Math.min(member.targetAchievement, 100)}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium text-gray-600">{member.targetAchievement}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${getTargetColor(member.targetAchievement)}`}>
                            {member.targetAchievement >= 100 ? 'Exceeded' : member.targetAchievement >= 75 ? 'On Track' : 'Behind'}
                          </span>
                        </td>
                      </tr>
                      {/* Expanded Row: Deal breakdown */}
                      {expandedRow === member.userId && (
                        <tr key={`${member.userId}-detail`}>
                          <td colSpan={12} className="bg-gray-50 px-8 py-4 max-sm:p-0">
                            <div className="space-y-4 max-sm:sticky max-sm:left-0 max-sm:w-[calc(100vw-34px)] max-sm:p-3">
                              {/* Qualified Leads */}
                              {member.qualifyingDeals.length > 0 && (
                                <div>
                                  <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">Qualified Leads ({member.qualifyingDeals.length})</h4>
                                  <div className="overflow-hidden rounded-lg border border-gray-200 bg-white max-sm:overflow-x-auto">
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="border-b bg-gray-50">
                                          <th className="px-3 py-2 text-left text-gray-500">Deal</th>
                                          <th className="px-3 py-2 text-left text-gray-500">Contact</th>
                                          <th className="px-3 py-2 text-left text-gray-500">Account</th>
                                          <th className="px-3 py-2 text-left text-gray-500">Stage</th>
                                          <th className="px-3 py-2 text-right text-gray-500">Value</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {member.qualifyingDeals.map((d) => (
                                          <tr key={d.id} className="border-b border-gray-100 last:border-0">
                                            <td className="px-3 py-2 font-medium text-gray-800 max-sm:min-w-[140px]">{d.title}</td>
                                            <td className="px-3 py-2 text-gray-600">{d.contact || '-'}</td>
                                            <td className="px-3 py-2 text-gray-600">{d.account || '-'}</td>
                                            <td className="px-3 py-2">
                                              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">{d.stage}</span>
                                            </td>
                                            <td className="px-3 py-2 text-right text-gray-700">{formatINR(d.value)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}

                              {/* Meetings */}
                              {member.meetingDealsList.length > 0 && (
                                <div>
                                  <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">Meetings Fixed ({member.meetingDealsList.length})</h4>
                                  <div className="overflow-hidden rounded-lg border border-gray-200 bg-white max-sm:overflow-x-auto">
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="border-b bg-gray-50">
                                          <th className="px-3 py-2 text-left text-gray-500">Deal</th>
                                          <th className="px-3 py-2 text-left text-gray-500">Contact</th>
                                          <th className="px-3 py-2 text-left text-gray-500">Stage</th>
                                          <th className="px-3 py-2 text-right text-gray-500">Value</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {member.meetingDealsList.map((d) => (
                                          <tr key={d.id} className="border-b border-gray-100 last:border-0">
                                            <td className="px-3 py-2 font-medium text-gray-800 max-sm:min-w-[140px]">{d.title}</td>
                                            <td className="px-3 py-2 text-gray-600">{d.contact || '-'}</td>
                                            <td className="px-3 py-2">
                                              <span className="rounded-full bg-yellow-50 px-2 py-0.5 text-yellow-700">{d.stage}</span>
                                            </td>
                                            <td className="px-3 py-2 text-right text-gray-700">{formatINR(d.value)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}

                              {/* Closures */}
                              {member.closedDealsList.length > 0 && (
                                <div>
                                  <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">Deals Won ({member.closedDealsList.length})</h4>
                                  <div className="overflow-hidden rounded-lg border border-gray-200 bg-white max-sm:overflow-x-auto">
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="border-b bg-gray-50">
                                          <th className="px-3 py-2 text-left text-gray-500">Deal</th>
                                          <th className="px-3 py-2 text-left text-gray-500">Contact</th>
                                          <th className="px-3 py-2 text-left text-gray-500">Account</th>
                                          <th className="px-3 py-2 text-right text-gray-500">Value</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {member.closedDealsList.map((d) => (
                                          <tr key={d.id} className="border-b border-gray-100 last:border-0">
                                            <td className="px-3 py-2 font-medium text-gray-800 max-sm:min-w-[140px]">{d.title}</td>
                                            <td className="px-3 py-2 text-gray-600">{d.contact || '-'}</td>
                                            <td className="px-3 py-2 text-gray-600">{d.account || '-'}</td>
                                            <td className="px-3 py-2 text-right text-gray-700">{formatINR(d.value)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}

                              {member.qualifyingDeals.length === 0 &&
                                member.meetingDealsList.length === 0 &&
                                member.closedDealsList.length === 0 && (
                                  <p className="text-xs text-gray-400">No qualifying activity in this period</p>
                                )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
